import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Provider } from 'react-redux';
import { store, useAppDispatch, useAppSelector } from './store';

import {
  loginUser,
  signupUser,
  verifyOtp,
  updateUserProfile,
  clearError,
  logout,
  restoreSession,
} from './store/authSlice';
import { fetchGamesAndPromos } from './store/gamesSlice';
import { fetchPaymentMethods } from './store/paymentsSlice';
import { fetchConversation, sendChatMessage, markAsRead } from './store/chatSlice';
import { fetchNotifications } from './store/notificationsSlice';

import { connectSocket, disconnectSocket } from './utils/socket';
import {
  initializePushNotifications,
  getNotificationPermissionState,
  getNotificationPromptDismissed,
  isPushSupported,
} from './utils/pushManager';
import { getPwaWorkspaceConfig } from './config/workspace-config';

import { ToastHUD } from './components/ToastHUD';
import { NotificationPromptModal } from './components/NotificationPromptModal';
import { InstallScreen } from './screens/InstallScreen';
import { SplashScreen } from './screens/SplashScreen';
import { AuthSelectScreen } from './screens/auth/AuthSelectScreen';
import { LoginScreen } from './screens/auth/LoginScreen';
import { SignupScreen } from './screens/auth/SignupScreen';
import { OtpScreen } from './screens/auth/OtpScreen';
import { ServerSettingsModal } from './screens/auth/ServerSettingsModal';
import { HomeScreen } from './screens/main/HomeScreen';
import { DepositScreen } from './screens/main/DepositScreen';
import { ChatScreen } from './screens/main/ChatScreen';
import { ProfileScreen } from './screens/main/ProfileScreen';
import { ActiveGameView } from './screens/main/ActiveGameView';
import { NotificationCenter } from './screens/main/NotificationCenter';

function AppContent() {
  const dispatch = useAppDispatch();

  // Redux States
  const { user, token, workspaceId, loading: authLoading, error: authError, verificationPhone, otpCode: smsOtpCode } = useAppSelector(state => state.auth);
  const { games, promotions, loading: gamesLoading, error: gamesError } = useAppSelector(state => state.games);
  const { paymentMethods, loading: paymentsLoading, error: paymentsError } = useAppSelector(state => state.payments);
  const { conversation, error: chatError } = useAppSelector(state => state.chat);
  const { items: notificationsItems } = useAppSelector(state => state.notifications);
  const unreadNotifCount = notificationsItems.filter(i => !i.read).length;

  // Active Workspace Config
  const wsConfig = getPwaWorkspaceConfig();
  const appName = wsConfig.appName || (workspaceId === 'workspace-2' ? 'CoinNova' : 'SpinNova');

  // Local Navigation State
  const [screen, setScreen] = useState<'install' | 'splash' | 'auth-select' | 'login' | 'signup' | 'otp' | 'main'>('splash');
  const [activeTab, setActiveTab] = useState<'home' | 'deposit' | 'chat' | 'profile'>('home');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showServerSettings, setShowServerSettings] = useState(false);

  // UI Selection States
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [gameFilter, setGameFilter] = useState('All');
  const [gameSearch, setGameSearch] = useState('');
  const [activeGameUrl, setActiveGameUrl] = useState<string | null>(null);

  // Chat/Upload UI States
  const [chatInput, setChatInput] = useState('');
  const [toastMsg, setToastMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Refs
  const pollingInterval = useRef<any>(null);
  const notifPollRef = useRef<any>(null);

  // Deep link route resolver
  const handleDeepLinkRoute = useCallback(() => {
    if (typeof window === 'undefined') return;

    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const tabParam = searchParams.get('tab');

    if (pathname === '/install') {
      setScreen('install');
      return;
    }

    if (pathname === '/chat' || pathname.startsWith('/chat/') || tabParam === 'chat') {
      setActiveTab('chat');
    } else if (pathname === '/deposit' || tabParam === 'deposit') {
      setActiveTab('deposit');
    } else if (pathname === '/profile' || tabParam === 'profile') {
      setActiveTab('profile');
    } else if (pathname === '/notifications') {
      setShowNotifications(true);
    } else if (pathname.startsWith('/games') || tabParam === 'games') {
      setActiveTab('home');
    }
  }, []);

  // Listen to popstate and initial deep link routing
  useEffect(() => {
    handleDeepLinkRoute();
    window.addEventListener('popstate', handleDeepLinkRoute);
    return () => window.removeEventListener('popstate', handleDeepLinkRoute);
  }, [handleDeepLinkRoute]);

  // Initial Boot / Restore Session — runs ONCE on mount only
  useEffect(() => {
    const init = async () => {
      const isInstallRoute = typeof window !== 'undefined' && window.location.pathname === '/install';
      if (isInstallRoute) {
        setScreen('install');
        return;
      }

      const resultAction = await dispatch(restoreSession());
      setTimeout(() => {
        setScreen(prev => {
          if (prev !== 'splash') return prev; // user already navigated — don't override
          if (restoreSession.fulfilled.match(resultAction) && resultAction.payload) {
            return 'main';
          }
          return 'login';
        });
      }, 1200);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch games, promos, payments once authenticated
  useEffect(() => {
    if (token) {
      dispatch(fetchGamesAndPromos());
      dispatch(fetchPaymentMethods());
      dispatch(fetchNotifications());
      setScreen('main');

      // Socket & Web Push Init
      connectSocket(token);
      initializePushNotifications(workspaceId);

      // Check if notification pre-permission prompt should be shown
      if (isPushSupported() && getNotificationPermissionState() === 'default' && !getNotificationPromptDismissed()) {
        const timer = setTimeout(() => {
          setShowNotificationPrompt(true);
        }, 2000);
        return () => clearTimeout(timer);
      }

      // Poll notifications
      notifPollRef.current = setInterval(() => {
        dispatch(fetchNotifications());
      }, 15000);

      // Poll support chat thread
      dispatch(fetchConversation());
      pollingInterval.current = setInterval(() => {
        dispatch(fetchConversation());
      }, 3000);
    } else {
      disconnectSocket();
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      if (notifPollRef.current) clearInterval(notifPollRef.current);
    }

    return () => {
      disconnectSocket();
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      if (notifPollRef.current) clearInterval(notifPollRef.current);
    };
  }, [token, workspaceId, dispatch]);

  // Read message handler
  useEffect(() => {
    if (activeTab === 'chat' && conversation?.id) {
      dispatch(markAsRead(conversation.id));
    }
  }, [activeTab, conversation?.id, dispatch]);

  // Toast for OTP code
  useEffect(() => {
    if (smsOtpCode && verificationPhone) {
      showToast(`OTP Code: ${smsOtpCode}`, 'success');
    }
  }, [smsOtpCode, verificationPhone]);

  // Toast for errors
  useEffect(() => {
    const err = authError || gamesError || paymentsError || chatError;
    if (err) {
      showToast(err, 'error');
      dispatch(clearError());
    }
  }, [authError, gamesError, paymentsError, chatError, dispatch]);

  const showToast = (text: string, type: 'success' | 'error') => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 3000);
  };

  const handleSaveServerIp = async (newIp: string): Promise<boolean> => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        if (newIp.trim()) {
          localStorage.setItem('custom_server_ip', newIp.trim());
          showToast(`Saved server IP: ${newIp.trim()}`, 'success');
        } else {
          localStorage.removeItem('custom_server_ip');
          showToast('Reset to default server IP', 'success');
        }
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  // Auth Handlers
  const handleLoginSubmit = (emailVal: string, passwordVal: string) => {
    if (!emailVal || !passwordVal) {
      showToast('Please enter your credentials', 'error');
      return;
    }
    dispatch(loginUser({ email: emailVal, password: passwordVal })).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        showToast('Login successful', 'success');
      }
    });
  };

  const handleSignupSubmit = (form: any) => {
    if (!form.name || !form.email || !form.phone || !form.password) {
      showToast('Please fill out all fields', 'error');
      return;
    }
    dispatch(signupUser(form)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled' && res.payload) {
        setScreen('otp');
      }
    });
  };

  const handleVerifyOtpSubmit = (codeVal: string) => {
    if (verificationPhone) {
      dispatch(verifyOtp({ phone: verificationPhone, code: codeVal || '0000' }));
    }
  };

  const handleSignOutAction = () => {
    dispatch(logout());
    setScreen('login');
    setSelectedPayment(null);
  };

  const handleUpdateProfile = async (data: { name?: string; password?: string; profileImage?: string | null }) => {
    const res = await dispatch(updateUserProfile(data));
    if (res.meta.requestStatus === 'fulfilled') {
      showToast('Profile updated successfully', 'success');
      return true;
    } else {
      const errorMsg = (res.payload as string) || 'Profile update failed';
      showToast(errorMsg, 'error');
      return false;
    }
  };

  const handleCopyToClipboard = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      showToast('Copied to clipboard!', 'success');
    }
  };

  const handleUploadPaymentReceipt = async () => {
    if (!conversation?.id || !selectedPayment) return;
    try {
      dispatch(sendChatMessage({
        conversationId: conversation.id,
        text: `System Alert: Deposit verification requested for provider [${selectedPayment.name}].`,
        imageUrl: selectedPayment.qr_code || '/icons/icon-192.png'
      })).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          showToast('Payment receipt shared in chat thread!', 'success');
          setActiveTab('chat');
        }
      });
    } catch (err: any) {
      showToast(err.message || 'Failed to submit receipt', 'error');
    }
  };

  const handleSendChatMessageAction = (imageUrl?: string | null) => {
    if (!chatInput.trim() && !imageUrl) return;
    if (!conversation?.id) return;
    const text = chatInput;
    setChatInput('');
    dispatch(sendChatMessage({ conversationId: conversation.id, text, imageUrl: imageUrl || undefined }));
  };

  const handleExitInstallScreen = () => {
    if (typeof window !== 'undefined' && window.location.pathname === '/install') {
      window.history.replaceState({}, '', '/');
    }
    setScreen(token ? 'main' : 'login');
  };

  const renderInnerTabContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <HomeScreen
            games={games}
            promotions={promotions}
            gamesLoading={gamesLoading}
            gameFilter={gameFilter}
            gameSearch={gameSearch}
            onSetGameFilter={setGameFilter}
            onPlayGame={(url) => setActiveGameUrl(url)}
          />
        );
      case 'deposit':
        return (
          <DepositScreen
            paymentMethods={paymentMethods}
            paymentsLoading={paymentsLoading}
            selectedPayment={selectedPayment}
            setSelectedPayment={setSelectedPayment}
            uploadingImage={false}
            onCopyToClipboard={handleCopyToClipboard}
            onUploadReceipt={handleUploadPaymentReceipt}
            onOpenPaymentPortal={(url) => setActiveGameUrl(url)}
          />
        );
      case 'chat':
        return (
          <ChatScreen
            conversation={conversation}
            chatInput={chatInput}
            setChatInput={setChatInput}
            onSendChatMessage={handleSendChatMessageAction}
          />
        );
      case 'profile':
        return (
          <ProfileScreen
            user={user}
            loading={authLoading}
            onUpdateProfile={handleUpdateProfile}
            onSignOut={handleSignOutAction}
          />
        );
    }
  };

  const renderAppLayout = () => {
    if (screen === 'install') {
      return <InstallScreen onContinueToApp={handleExitInstallScreen} />;
    }

    if (screen === 'splash') {
      return <SplashScreen />;
    }

    if (screen === 'auth-select') {
      return <AuthSelectScreen onLogin={() => setScreen('login')} onSignup={() => setScreen('signup')} />;
    }

    if (screen === 'login') {
      return (
        <LoginScreen
          loading={authLoading}
          onSubmit={handleLoginSubmit}
          onSignup={() => setScreen('signup')}
        />
      );
    }

    if (screen === 'signup') {
      return (
        <SignupScreen
          loading={authLoading}
          onSubmit={handleSignupSubmit}
          onLogin={() => setScreen('login')}
        />
      );
    }

    if (screen === 'otp') {
      return (
        <OtpScreen
          loading={authLoading}
          defaultCode={smsOtpCode || ''}
          onSubmit={handleVerifyOtpSubmit}
          onBack={() => setScreen('login')}
        />
      );
    }

    const supportUnreadCount = conversation?.unreadCount || 0;

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
        {/* Header Bar */}
        <header className="pwa-header">
          <div>
            <div className="pwa-header-title">
              {appName.toUpperCase()}
            </div>
            <div className="pwa-header-subtitle">Multi-tenant Game Console</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button onClick={() => setShowNotifications(true)} className="pwa-notif-btn">
              🔔
              {unreadNotifCount > 0 && <span className="pwa-badge">{unreadNotifCount}</span>}
            </button>
            <button onClick={() => setActiveTab('profile')} className="pwa-avatar-circle">
              {user?.name?.charAt(0) || 'P'}
            </button>
          </div>
        </header>

        {/* Global Search Bar on Home tab */}
        {activeTab === 'home' && (
          <div className="pwa-search-bar">
            <input
              type="text"
              className="pwa-search-input"
              placeholder="Search active games..."
              value={gameSearch}
              onChange={(e) => setGameSearch(e.target.value)}
            />
          </div>
        )}

        {/* Screen Switch Content */}
        {renderInnerTabContent()}

        {/* Bottom Navigation Bar */}
        <nav className="pwa-bottom-nav">
          <button onClick={() => setActiveTab('home')} className="pwa-nav-item">
            <div className={`pwa-nav-icon-box ${activeTab === 'home' ? 'active' : ''}`}>🎮</div>
            <span className={`pwa-nav-label ${activeTab === 'home' ? 'active' : ''}`}>GAMES</span>
          </button>

          <button onClick={() => setActiveTab('deposit')} className="pwa-nav-item">
            <div className={`pwa-nav-icon-box ${activeTab === 'deposit' ? 'active' : ''}`}>💰</div>
            <span className={`pwa-nav-label ${activeTab === 'deposit' ? 'active' : ''}`}>DEPOSIT</span>
          </button>

          <button onClick={() => setActiveTab('chat')} className="pwa-nav-item">
            <div className={`pwa-nav-icon-box ${activeTab === 'chat' ? 'active' : ''}`}>
              💬
              {supportUnreadCount > 0 && (
                <span className="pwa-badge" style={{ top: '-4px', right: '-8px' }}>{supportUnreadCount}</span>
              )}
            </div>
            <span className={`pwa-nav-label ${activeTab === 'chat' ? 'active' : ''}`}>SUPPORT</span>
          </button>

          <button onClick={() => setActiveTab('profile')} className="pwa-nav-item">
            <div className={`pwa-nav-icon-box ${activeTab === 'profile' ? 'active' : ''}`}>👤</div>
            <span className={`pwa-nav-label ${activeTab === 'profile' ? 'active' : ''}`}>PROFILE</span>
          </button>
        </nav>

        {showNotifications && (
          <NotificationCenter
            onClose={() => setShowNotifications(false)}
            onNavigateTab={(tab) => setActiveTab(tab)}
          />
        )}

        {/* Notification Pre-Permission Onboarding Modal */}
        {showNotificationPrompt && (
          <NotificationPromptModal
            workspaceId={workspaceId}
            onClose={() => setShowNotificationPrompt(false)}
            onSuccess={() => showToast('Push notifications enabled!', 'success')}
          />
        )}

        {/* Game Simulator Overlay */}
        <ActiveGameView
          activeGameUrl={activeGameUrl}
          onClose={() => setActiveGameUrl(null)}
        />
      </div>
    );
  };

  return (
    <div className="pwa-app-frame">
      {renderAppLayout()}

      {/* Developer / Server Settings Button — strictly available in development mode only */}
      {import.meta.env.DEV && !token && screen !== 'splash' && (
        <button
          onClick={() => setShowServerSettings(true)}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '20px',
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            border: '1px solid rgba(255, 255, 255, 0.15)',
            fontSize: '18px',
            color: '#fff',
            cursor: 'pointer',
          }}
          aria-label="Developer Server Settings"
        >
          ⚙️
        </button>
      )}

      {/* Developer Server Settings Modal — strictly mounted in development mode only */}
      {import.meta.env.DEV && (
        <ServerSettingsModal
          visible={showServerSettings}
          onClose={() => setShowServerSettings(false)}
          onSave={handleSaveServerIp}
        />
      )}

      {toastMsg && <ToastHUD message={toastMsg.text} type={toastMsg.type} />}
    </div>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}
